<template>
    <div
        class="input_medium1 proxima_nova"
        :class="disabled ? 'opacity-[0.65] pointer-events-none cursor-not-allowed' : ''"
    >
        <div class="dropdown-section">
            <a-select
                :value="selectedOption"
                @change="handleDropdownChange"
                class="currency-select"
                :bordered="false"
                :disabled="disabled"
                popupClassName="currency-dropdown-popup"
                optionLabelProp="labelToShow"
                :virtual="false"
                :getPopupContainer="getPopupContainer"
            >
                <template #suffixIcon>
                    <ThemeImage
                        :icon="imageNames.BOTTOM_ARROW"
                        alt="bottom_white_arrow"
                        class="size-4"
                    />
                </template>
                <a-select-option 
                    v-for="option in dropdownOptions" 
                    :key="option.value"
                    :value="option.value"
                    :labelToShow="option.labelToShow"
                >
                    {{ option.label }}
                </a-select-option>
            </a-select>
        </div>
        
        <div class="input-section">
            <span v-if="showSymbol" class="symbol">{{ currentSymbol }}</span>
            <a-input
                :value="inputValue"
                @input="handleInputChange"
                @keyup="handleKeyup"
                @keypress="handleKeypress"
                @paste="handlePaste"
                :placeholder="placeholder"
                :bordered="false"
                :disabled="disabled"
                :inputmode="numericOnly ? 'decimal' : undefined"
                class="amount-input"
            />
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
    shouldBlockNumericKeypress,
    sanitizeNumericPaste,
} from '@/src/utils/numericInputUtils.js';

const props = defineProps({
    dropdownOptions: {
        type: Array,
        required: true,
    },
    selectedOption: {
        type: String,
        required: true,
    },
    inputValue: {
        type: [String, Number, null],
        default: null,
    },
    showSymbol: {
        type: Boolean,
        default: true,
    },
    placeholder: {
        type: String,
        default: '0.00',
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    // When true, the input only accepts digits, a single decimal separator,
    // and an optional leading minus. Non-numeric keystrokes are swallowed
    // before the input value is ever updated.
    numericOnly: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:selectedOption', 'update:inputValue', 'keyup.enter']);

// Compute the symbol based on selected option
const currentSymbol = computed(() => {
    const selected = props.dropdownOptions.find(
        option => option.value === props.selectedOption
    );
    return selected?.symbol || '';
});

const handleDropdownChange = (value) => {
    emit('update:selectedOption', value);
};

const handleInputChange = (e) => {
    emit('update:inputValue', e.target.value);
};

const handleKeyup = (e) => {
    // Forward keyboard events to parent
    if (e.key === 'Enter') {
        emit('keyup.enter', e);
    }
};

const handleKeypress = (e) => {
    if (!props.numericOnly) return;
    if (shouldBlockNumericKeypress(e, props.inputValue)) e.preventDefault();
};

const handlePaste = (e) => {
    if (!props.numericOnly) return;
    e.preventDefault();
    const pasted = (e.clipboardData || window.clipboardData).getData('text') || '';
    emit(
        'update:inputValue',
        sanitizeNumericPaste(pasted, props.inputValue, e.target.selectionStart, e.target.selectionEnd),
    );
};

const getPopupContainer = (trigger) => {
    // Safely get document.body, fallback to trigger parent if not available
    return typeof document !== 'undefined' ? document.body : trigger.parentNode;
};
</script>

<style scoped>
.input_medium1{
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  background: var(--white-opacity-4);
  color: var(--foreground-rgb) !important;
  font-family: "Proxima Nova", sans-serif !important;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  border: 1px solid var(--white-opacity-8);
}

.dropdown-section {
    padding-left:12px;
    display: flex;
    align-items: center;
}

.dropdown-section :deep(.currency-select) {
    min-width: 60px;
}

.dropdown-section :deep(.ant-select-selector) {
    padding: 0 !important;
    background: transparent !important;
}

.input-section {
    display: flex;
    align-items: center;
    flex: 1;
    gap: 4px;
    margin-left:-10px;
}

.symbol {
    font-size: 14px;
    font-weight: 400;
    color: var(--secondary_color);
    user-select: none;
}

.input-section :deep(.amount-input) {
    flex: 1;
}

.input-section :deep(.ant-input) {
    padding: 0 !important;
    font-size: 14px;
}

</style>

<style>
.currency-dropdown-popup {
    width: 213px !important;
}

.currency-dropdown-popup .ant-select-dropdown {
    padding: 12px 8px 12px 8px;
    border-bottom: 1px solid var(--white-opacity-8);
    background: var(--white-opacity-4);
    border-radius: 8px;
}

.currency-dropdown-popup .rc-virtual-list-holder {
    max-height: 156px !important;
    overflow-y: scroll !important;
    padding-right: 4px;
}

.currency-dropdown-popup .ant-select-item {
    padding: 8px 12px;
    font-size: 14px;
    border-radius: 4px;
    font-family: "Proxima Nova", sans-serif;
}

.currency-dropdown-popup .ant-select-item-option-selected {
    background-color: var(--white-opacity-8);
}

.currency-dropdown-popup .ant-select-item-option-active {
    background-color: var(--white-opacity-8);
}

.currency-dropdown-popup .rc-virtual-list-holder::-webkit-scrollbar {
    width: 6px;
    display: block;
}

.currency-dropdown-popup .rc-virtual-list-holder::-webkit-scrollbar-thumb {
    background: var(--white-opacity-8);
    border-radius: 3px;
}

.currency-dropdown-popup .rc-virtual-list-holder::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
}
</style>
